<template>
  <TemplateBase>
    <main class="relative z-0 col-span-full row-start-2">
      <!-- Header -->
      <MoleculeHeaderOverview
        class="mb-8 mt-16 sm:mb-12 md:mb-8 lg:mt-20"
        :title="page.title"
        :subtitle="page.subtitle"
        :introduction="page.introduction"
        :position-text-left="true"
      />

      <section id="article-overview-filters" class="relative mx-auto px-4 2xl:px-64 sm:px-6 md:container md:px-5 lg:px-10 xl:px-16">
        <hr class="mt-6 w-full border-t-2 border-solid" :class="{ 'border-white': showFilters, 'border-transparent': !showFilters }" />
        <div class="flex flex-col py-5 md:flex-row md:items-start" :class="{ 'md:pb-0': activeFacets.length }">
          <!-- ACTIVE FACETS -->
          <div v-if="activeFacets.length" class="md:order-0 order-1 mt-2 md:mt-0 lg:flex">
            <h2 class="mb-3 text-app-green font-semibold leading-tight md:shrink-0 md:mr-2 md:mt-1 md:text-lg">
              {{ tc_('total-hits', totalHits) }}
            </h2>
            <ul class="flex flex-auto flex-wrap md:mr-4">
              <li v-for="{ name, label, value } of activeFacets" :key="value" class="mb-2 mr-2 md:mb-4 md:mr-4" @click="toggleFacet({ name, value })">
                <MoleculeFilter :selected="true" :text="label || value" />
              </li>
              <li class="mb-2 mr-2 md:mb-4 md:mr-4" @click="reset">
                <MoleculeFilter :selected="true" :text="t_('reset')" />
              </li>
            </ul>
          </div>

          <div id="article-overview-btn-filter" class="order-0 flex items-center ml-auto cursor-pointer md:order-1" @click="showFilters = !showFilters">
            <AtomIconX v-if="showFilters" class="mr-2 w-3 h-3 text-app-green" />
            <AtomIconAppFilter v-else class="mr-2 w-3 h-3 text-app-green" />
            <span class="underline text-base font-semibold uppercase">{{ t_('filters') }}</span>
          </div>
        </div>
        <hr v-if="showFilters" class="mb-4 w-full border-t-2 border-solid border-white" />
      </section>

      <!-- AVAILABLE FILTERS -->
      <template v-if="showFilters">
        <section
          v-for="{ name, values } in facets"
          v-show="values.length"
          id="article-overview-filters-available"
          :key="name"
          class="relative mx-auto px-4 2xl:px-64 sm:px-6 md:container md:px-5 lg:px-10 xl:px-16"
        >
          <h2 class="mb-3 font-bold leading-tight md:text-2xl">{{ t_(name) }}</h2>
          <ul class="flex flex-wrap mb-3">
            <li v-for="{ active, value, label } of values" :key="value" class="mb-2 mr-2 md:mb-4 md:mr-4">
              <MoleculeFilter :selected="active" :text="label || value" @click="toggleFacet({ name, value })" />
            </li>
          </ul>

          <hr class="mb-4 w-full border-t-2 border-solid border-white" />
        </section>
      </template>

      <!-- RESULTS -->
      <template v-if="articles.length > 0">
        <MoleculeCollage
          id="article-overview-results"
          :entries="articles"
          class="relative z-10 mb-12 mx-auto 2xl:px-64 md:container md:px-5 lg:px-10 xl:px-16"
          :label="page.articleOverviewTitleLastArticles || null"
        >
          <template #large="{ entry }">
            <MoleculeCardOverviewCover
              v-if="entry.title"
              :entry-url="entry.url"
              :entry-title="entry.title"
              :entry-type="get(entry, 'articleCategories.title')"
              :image="entry.largeImage"
              :collage-large="true"
            />
          </template>
          <template #medium="{ entry }">
            <MoleculeCardOverviewCover
              v-if="entry.title"
              :entry-url="entry.url"
              :entry-title="entry.title"
              :entry-type="get(entry, 'articleCategories.title')"
              :image="entry.mediumImage"
              :collage-medium="true"
            />
          </template>
          <template #small="{ entry }">
            <MoleculeCardOverviewCover
              v-if="entry.title"
              :entry-url="entry.url"
              :entry-title="entry.title"
              :entry-type="get(entry, 'articleCategories.title')"
              :image="entry.smallImage"
              class="hidden md:flex"
            />
            <MoleculeCardOverviewWide
              v-if="entry.title"
              :url="entry.url"
              :title="entry.title"
              :type="get(entry, 'articleCategories.title')"
              :image="entry.smallImage"
              :img-alt="get(entry, 'articleCategories.title')"
              class="md:hidden"
            />
          </template>
        </MoleculeCollage>
      </template>

      <!-- PAGINATION -->
      <section id="article-overview-paging" class="container relative mb-12 mx-auto">
        <OrganismPagination :count="totalHits" :limit="hitsPerPage" :page="pageNumber" @click="({ page }) => setPageNumber(page)" />
      </section>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import get from 'lodash/get'
import { useRoute, useRouter } from 'vue-router'
import { useArticleOverviewStore } from '~/store/article-overview'
import { setupTranslationPrefixer } from '~/composables/util'
import { routerQueryIsEqual } from '~/composables/setup-dynamic-url'

interface Props {
  page: {
    title: string
    subtitle?: string
    introduction?: string
    articleOverviewTitleLastArticles?: string
  }
}

defineProps<Props>()

const route = useRoute()
const router = useRouter()
const articleOverviewStore = useArticleOverviewStore()

const showFilters = ref(false)

const activeFacets = computed(() => articleOverviewStore.activeFacets)
const articles = computed(() => articleOverviewStore.articles)
const facets = computed(() => articleOverviewStore.getFacets)
const hitsPerPage = computed(() => articleOverviewStore.hitsPerPage)
const pageNumber = computed(() => articleOverviewStore.pageNumber)
const totalHits = computed(() => articleOverviewStore.totalHits)
const urlParameters = computed(() => articleOverviewStore.urlParameters)

watch(urlParameters, (query) => {
  if (!routerQueryIsEqual(route.query, query)) {
    router.push({ query })
  }
})

const { t_, tc_ } = setupTranslationPrefixer('ArticleOverview')

const reset = articleOverviewStore.reset
const setPageNumber = articleOverviewStore.setPageNumber
const toggleFacet = articleOverviewStore.toggleFacet
</script>
